import { CheckCircle2, CircleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import SelectedFileDisplay from '@/components/common/SelectedFileDisplay';
import Table from '@/components/common/Table';

export interface ImportValidationRow {
    row: number;
    field: string;
    description: string;
}

interface ImportValidationStepProps {
    file: File;
    readyCount: number;
    errorCount: number;
    errors: ImportValidationRow[];
}

const ImportValidationStep = ({ file, readyCount, errorCount, errors }: ImportValidationStepProps) => {
    const { t } = useTranslation('performanceResult');
    const hasErrors = errorCount > 0;
    const noNewResults = !hasErrors && readyCount === 0;
    const noReadyWithErrors = hasErrors && readyCount === 0;

    return (
        <div className="w-full">
            {/* File info */}
            <div className="mt-6 mb-4">
                <SelectedFileDisplay file={file} />
            </div>

            {/* Validation banner */}
            <div
                className={`mb-4 rounded-xl px-4 py-4 ${hasErrors ? 'bg-red-50 text-red-500' : noNewResults ? 'bg-amber-50 text-amber-800' : 'bg-emerald-50 text-emerald-800'}`}
            >
                <div className="flex items-center gap-2">
                    {hasErrors ? (
                        <CircleAlert className="size-4" aria-hidden />
                    ) : noNewResults ? (
                        <CircleAlert className="size-4" aria-hidden />
                    ) : (
                        <CheckCircle2 className="size-4" aria-hidden />
                    )}
                    <span className="text-base font-semibold text-gray-900">
                        {hasErrors
                            ? t('bulk.validation.validationCompleteWithErrors', { errors: errorCount })
                            : noNewResults
                              ? t('bulk.validation.noNewResultsTitle')
                              : t('bulk.validation.validationComplete')}
                    </span>
                </div>
                <div className="mt-1 text-base font-medium text-neutral-500">
                    {noReadyWithErrors
                        ? t('bulk.validation.noNewResultsWithErrorsDescription')
                        : noNewResults
                          ? t('bulk.validation.noNewResultsDescription')
                          : t('bulk.validation.readyToImportCount', { count: readyCount })}
                </div>
            </div>

            {/* Error table */}
            {hasErrors && (
                <div className="max-h-[300px] overflow-y-auto">
                    <Table
                        columns={[
                            {
                                title: t('bulk.validation.row'),
                                dataIndex: 'row',
                                key: 'row',
                                render: (text: string) => <span className="text-neutral-500">{text}</span>,
                            },
                            {
                                title: t('bulk.validation.field'),
                                dataIndex: 'field',
                                key: 'field',
                                render: (text: string) => <span className="text-neutral-500">{text}</span>,
                            },
                            {
                                title: t('bulk.validation.description'),
                                dataIndex: 'description',
                                key: 'description',
                                render: (text: string) => <span className="text-neutral-500">{text}</span>,
                            },
                        ]}
                        dataSource={errors}
                    />
                </div>
            )}
        </div>
    );
};

export default ImportValidationStep;
